import { useEffect, useMemo, useRef, useState } from 'react';
import { Palette, Upload, X, Moon, Sun, Monitor } from 'lucide-react';
import {
  useProjectBranding,
  useRemoveProjectLogo,
  useUpdateProjectBranding,
  useUploadProjectLogo,
} from '../../hooks/useProjectBranding';
import { Button, Field, FormSection, Input } from '../../ui';
import {
  BRANDING_COLOR_KEYS,
  BRANDING_LAYOUTS,
  BRANDING_THEMES,
  isValidHexColor,
  type BrandingColorKey,
  type BrandingColors,
  type BrandingLayout,
  type BrandingTheme,
  type ProjectBrandingUpdate,
} from '../../types/projectBranding';
import './ProjectBrandingSettings.css';

interface ProjectBrandingSettingsProps {
  projectId: string;
  canManage: boolean;
}

interface ColorMeta {
  label: string;
  hint: string;
  lightPreview: string;
  darkPreview: string;
}

const COLOR_LABELS: Record<BrandingColorKey, ColorMeta> = {
  primary_color: {
    label: 'Primary',
    hint: 'Submit buttons, links, focus rings.',
    lightPreview: '#4f46e5',
    darkPreview: '#818cf8',
  },
  primary_hover_color: {
    label: 'Primary hover',
    hint: 'Hover state of primary buttons.',
    lightPreview: '#4338ca',
    darkPreview: '#6366f1',
  },
  accent_color: {
    label: 'Accent',
    hint: 'Decorative highlights and secondary actions.',
    lightPreview: '#4f46e5',
    darkPreview: '#818cf8',
  },
  background_color: {
    label: 'Background',
    hint: 'Page background behind the auth card.',
    lightPreview: '#fafafa',
    darkPreview: '#09090b',
  },
  foreground_color: {
    label: 'Foreground',
    hint: 'Body text color on the auth card.',
    lightPreview: '#1a1a1a',
    darkPreview: '#fafafa',
  },
  error_color: {
    label: 'Error',
    hint: 'Inline error messages.',
    lightPreview: '#b91c1c',
    darkPreview: '#f87171',
  },
};

const LAYOUT_LABELS: Record<BrandingLayout, { label: string; hint: string }> = {
  centered: {
    label: 'Centered card',
    hint: 'A single card centered on the page. Default; works best for marketing-style brands.',
  },
  split: {
    label: 'Split panel',
    hint: 'A left brand panel and a right form column. Stacks to a single column on mobile.',
  },
};

const THEME_LABELS: Record<BrandingTheme, { label: string; hint: string; Icon: typeof Sun }> = {
  light: { label: 'Light', hint: 'Always render the light palette.', Icon: Sun },
  dark: { label: 'Dark', hint: 'Always render the dark palette.', Icon: Moon },
  auto: {
    label: 'Auto (system)',
    hint: 'Follow the end-user’s prefers-color-scheme.',
    Icon: Monitor,
  },
};

type ColorDraft = Record<BrandingColorKey, string>;

function colorsToDraft(colors: BrandingColors): ColorDraft {
  return BRANDING_COLOR_KEYS.reduce((acc, key) => {
    acc[key] = colors[key] ?? '';
    return acc;
  }, {} as ColorDraft);
}

function buildPreviewColors(draft: ColorDraft, theme: 'light' | 'dark') {
  return BRANDING_COLOR_KEYS.reduce(
    (acc, key) => {
      const value = draft[key].trim();
      acc[key] = isValidHexColor(value)
        ? value
        : theme === 'dark'
          ? COLOR_LABELS[key].darkPreview
          : COLOR_LABELS[key].lightPreview;
      return acc;
    },
    {} as Record<BrandingColorKey, string>
  );
}

export function ProjectBrandingSettings({ projectId, canManage }: ProjectBrandingSettingsProps) {
  const { data: branding, isLoading, isError, error } = useProjectBranding(projectId);
  const updateBranding = useUpdateProjectBranding();
  const uploadLogo = useUploadProjectLogo();
  const removeLogo = useRemoveProjectLogo();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const emptyColors = useMemo(
    () =>
      BRANDING_COLOR_KEYS.reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, {} as ColorDraft),
    []
  );

  const [appName, setAppName] = useState('');
  const [layout, setLayout] = useState<BrandingLayout>('centered');
  const [defaultTheme, setDefaultTheme] = useState<BrandingTheme>('light');
  const [allowUserOverride, setAllowUserOverride] = useState(true);
  const [colorDraft, setColorDraft] = useState<ColorDraft>(emptyColors);
  const [darkColorDraft, setDarkColorDraft] = useState<ColorDraft>(emptyColors);
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!branding) return;
    setAppName(branding.app_name ?? '');
    setLayout(branding.layout);
    setDefaultTheme(branding.default_theme);
    setAllowUserOverride(branding.allow_user_theme_override);
    setColorDraft(colorsToDraft(branding.colors));
    setDarkColorDraft(colorsToDraft(branding.colors_dark));
    // Preview the theme the project will actually show first to end-users.
    setPreviewTheme(branding.default_theme === 'dark' ? 'dark' : 'light');
  }, [branding]);

  const isDirty = useMemo(() => {
    if (!branding) return false;
    if ((appName.trim() || null) !== branding.app_name) return true;
    if (layout !== branding.layout) return true;
    if (defaultTheme !== branding.default_theme) return true;
    if (allowUserOverride !== branding.allow_user_theme_override) return true;
    if (
      BRANDING_COLOR_KEYS.some(k => (colorDraft[k].trim() || null) !== (branding.colors[k] ?? null))
    ) {
      return true;
    }
    if (
      BRANDING_COLOR_KEYS.some(
        k => (darkColorDraft[k].trim() || null) !== (branding.colors_dark[k] ?? null)
      )
    ) {
      return true;
    }
    return false;
  }, [branding, appName, layout, defaultTheme, allowUserOverride, colorDraft, darkColorDraft]);

  const previewColorsLight = useMemo(() => buildPreviewColors(colorDraft, 'light'), [colorDraft]);
  const previewColorsDark = useMemo(
    () => buildPreviewColors(darkColorDraft, 'dark'),
    [darkColorDraft]
  );
  const activePreviewColors = previewTheme === 'dark' ? previewColorsDark : previewColorsLight;

  const handleColorChange = (theme: 'light' | 'dark', key: BrandingColorKey, value: string) => {
    if (theme === 'dark') {
      setDarkColorDraft(prev => ({ ...prev, [key]: value }));
    } else {
      setColorDraft(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleClearColor = (theme: 'light' | 'dark', key: BrandingColorKey) => {
    if (theme === 'dark') {
      setDarkColorDraft(prev => ({ ...prev, [key]: '' }));
    } else {
      setColorDraft(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validateColorDraft = (draft: ColorDraft, labelPrefix: string): string | null => {
    for (const key of BRANDING_COLOR_KEYS) {
      const v = draft[key].trim();
      if (v && !isValidHexColor(v)) {
        return `${labelPrefix} ${COLOR_LABELS[key].label} must be a hex color like #4f46e5.`;
      }
    }
    return null;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    if (!canManage || !branding) return;

    const lightError = validateColorDraft(colorDraft, 'Light');
    if (lightError) return setValidationError(lightError);
    const darkError = validateColorDraft(darkColorDraft, 'Dark');
    if (darkError) return setValidationError(darkError);

    const trimmedName = appName.trim();
    if (trimmedName.length > branding.max_app_name_length) {
      setValidationError(`App name must be ${branding.max_app_name_length} characters or fewer.`);
      return;
    }

    const payload: ProjectBrandingUpdate = {
      app_name: trimmedName || null,
      layout,
      default_theme: defaultTheme,
      allow_user_theme_override: allowUserOverride,
    };
    for (const key of BRANDING_COLOR_KEYS) {
      payload[key] = colorDraft[key].trim() || null;
    }
    const darkPayload: Partial<BrandingColors> = {};
    for (const key of BRANDING_COLOR_KEYS) {
      darkPayload[key] = darkColorDraft[key].trim() || null;
    }
    payload.colors_dark = darkPayload;

    try {
      await updateBranding.mutateAsync({ projectId, data: payload });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      /* surfaced via mutation error below */
    }
  };

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadLogo.mutateAsync({ projectId, file });
    } catch {
      /* surfaced via mutation error below */
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!canManage) return;
    await removeLogo.mutateAsync(projectId);
  };

  if (isLoading) {
    return <p className="project-branding-settings__loading">Loading branding…</p>;
  }
  if (isError || !branding) {
    return (
      <p className="project-branding-settings__error">
        {error?.message || 'Failed to load branding.'}
      </p>
    );
  }

  const previewAppName = appName.trim() || branding.resolved_app_name;

  const renderColorRow = (theme: 'light' | 'dark', key: BrandingColorKey) => {
    const meta = COLOR_LABELS[key];
    const draft = theme === 'dark' ? darkColorDraft : colorDraft;
    const value = draft[key];
    const fallback = theme === 'dark' ? meta.darkPreview : meta.lightPreview;
    const swatch = isValidHexColor(value) ? value : fallback;
    return (
      <div key={`${theme}-${key}`} className="project-branding-settings__color-row">
        <div className="project-branding-settings__color-meta">
          <span className="project-branding-settings__color-label">{meta.label}</span>
          <span className="project-branding-settings__color-hint">{meta.hint}</span>
        </div>
        <input
          type="color"
          aria-label={`${theme === 'dark' ? 'Dark' : 'Light'} ${meta.label} color`}
          value={swatch}
          disabled={!canManage}
          onChange={e => handleColorChange(theme, key, e.target.value)}
          className="project-branding-settings__color-swatch"
        />
        <Input
          type="text"
          placeholder={fallback}
          value={value}
          disabled={!canManage}
          onChange={e => handleColorChange(theme, key, e.target.value)}
          className="project-branding-settings__color-text"
          aria-label={`${theme === 'dark' ? 'Dark' : 'Light'} ${meta.label} hex`}
        />
        {value && (
          <button
            type="button"
            className="project-branding-settings__color-clear"
            aria-label={`Clear ${theme === 'dark' ? 'dark' : 'light'} ${meta.label}`}
            onClick={() => handleClearColor(theme, key)}
            disabled={!canManage}
          >
            <X size={12} aria-hidden="true" />
          </button>
        )}
      </div>
    );
  };

  return (
    <FormSection
      title={
        <span className="project-branding-settings__title">
          <Palette size={18} aria-hidden="true" /> Branding
        </span>
      }
      description="Customize the logo, colors, layout, and theme shown on the hosted sign-in pages for this project."
    >
      <form onSubmit={handleSave} className="project-branding-settings__form">
        <div className="project-branding-settings__layout">
          <div className="project-branding-settings__controls">
            <Field
              label="App display name"
              htmlFor="brandingAppName"
              hint={`Shown in copy like "Sign in to <name>". Leave blank to use the project name (${branding.resolved_app_name}). Max ${branding.max_app_name_length} characters.`}
            >
              <Input
                id="brandingAppName"
                type="text"
                value={appName}
                placeholder={branding.resolved_app_name}
                disabled={!canManage}
                maxLength={branding.max_app_name_length}
                onChange={e => setAppName(e.target.value)}
              />
            </Field>

            <fieldset
              className="project-branding-settings__logo"
              disabled={!canManage || uploadLogo.isPending || removeLogo.isPending}
            >
              <legend>Logo</legend>
              <div className="project-branding-settings__logo-row">
                {branding.logo_url ? (
                  <img
                    src={branding.logo_url}
                    alt={`${branding.resolved_app_name} logo`}
                    className="project-branding-settings__logo-img"
                  />
                ) : (
                  <div className="project-branding-settings__logo-placeholder" aria-hidden="true">
                    {branding.resolved_app_name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="project-branding-settings__logo-actions">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoFileChange}
                    className="project-branding-settings__logo-input"
                    aria-label="Upload logo"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!canManage || uploadLogo.isPending}
                  >
                    <Upload size={14} aria-hidden="true" />
                    {uploadLogo.isPending ? 'Uploading…' : 'Upload logo'}
                  </Button>
                  {branding.logo_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleRemoveLogo}
                      disabled={!canManage || removeLogo.isPending}
                    >
                      <X size={14} aria-hidden="true" />
                      {removeLogo.isPending ? 'Removing…' : 'Remove'}
                    </Button>
                  )}
                </div>
              </div>
              <p className="project-branding-settings__logo-hint">
                PNG, JPEG, GIF, WebP, or SVG. Max 5 MB.
              </p>
              {(uploadLogo.isError || removeLogo.isError) && (
                <p className="project-branding-settings__error" role="alert">
                  {(uploadLogo.error as Error | undefined)?.message ||
                    (removeLogo.error as Error | undefined)?.message ||
                    'Logo update failed.'}
                </p>
              )}
            </fieldset>

            <fieldset className="project-branding-settings__layout-section" disabled={!canManage}>
              <legend>Layout & theme</legend>

              <div className="project-branding-settings__layout-choices">
                {BRANDING_LAYOUTS.map(value => (
                  <label
                    key={value}
                    className={`project-branding-settings__layout-card ${layout === value ? 'is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="branding-layout"
                      value={value}
                      checked={layout === value}
                      disabled={!canManage}
                      onChange={() => setLayout(value)}
                    />
                    <span
                      className={`project-branding-settings__layout-thumb project-branding-settings__layout-thumb--${value}`}
                      aria-hidden="true"
                    />
                    <span className="project-branding-settings__layout-label">
                      {LAYOUT_LABELS[value].label}
                    </span>
                    <span className="project-branding-settings__layout-hint">
                      {LAYOUT_LABELS[value].hint}
                    </span>
                  </label>
                ))}
              </div>

              <div
                className="project-branding-settings__theme-row"
                role="radiogroup"
                aria-label="Default theme"
              >
                <span className="project-branding-settings__field-label">Default theme</span>
                <div className="project-branding-settings__theme-choices">
                  {BRANDING_THEMES.map(value => {
                    const meta = THEME_LABELS[value];
                    const Icon = meta.Icon;
                    return (
                      <label
                        key={value}
                        className={`project-branding-settings__theme-chip ${defaultTheme === value ? 'is-selected' : ''}`}
                        title={meta.hint}
                      >
                        <input
                          type="radio"
                          name="branding-default-theme"
                          value={value}
                          checked={defaultTheme === value}
                          disabled={!canManage}
                          onChange={() => setDefaultTheme(value)}
                        />
                        <Icon size={14} aria-hidden="true" />
                        {meta.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              <label className="project-branding-settings__toggle-row">
                <input
                  type="checkbox"
                  checked={allowUserOverride}
                  disabled={!canManage}
                  onChange={e => setAllowUserOverride(e.target.checked)}
                />
                <span>
                  <strong>Show an end-user theme toggle (advanced).</strong>
                  <span className="project-branding-settings__color-hint">
                    {' '}
                    Off by default. Most apps drive theme themselves — pass{' '}
                    <code>
                      theme: &quot;light&quot; | &quot;dark&quot; | &quot;auto&quot;
                    </code> to <code>loginWithRedirect()</code>, or append{' '}
                    <code>?theme=&hellip;</code> to the auth URL. Turn this on only if you want
                    end-users to be able to flip theme independently of the surrounding app.
                  </span>
                </span>
              </label>
            </fieldset>

            <fieldset className="project-branding-settings__colors" disabled={!canManage}>
              <legend>Light theme colors</legend>
              {BRANDING_COLOR_KEYS.map(key => renderColorRow('light', key))}
            </fieldset>

            <fieldset className="project-branding-settings__colors" disabled={!canManage}>
              <legend>Dark theme colors</legend>
              <p className="project-branding-settings__color-hint">
                Customize the palette shown when the active theme is dark. Leave blank to inherit
                the default dark palette.
              </p>
              {BRANDING_COLOR_KEYS.map(key => renderColorRow('dark', key))}
            </fieldset>

            {validationError && (
              <p className="project-branding-settings__error" role="alert">
                {validationError}
              </p>
            )}
            {updateBranding.isError && (
              <p className="project-branding-settings__error" role="alert">
                {(updateBranding.error as Error)?.message || 'Failed to save branding.'}
              </p>
            )}
            {saved && (
              <p className="project-branding-settings__saved" role="status">
                Branding saved.
              </p>
            )}

            <div className="project-branding-settings__actions">
              <Button type="submit" disabled={!canManage || !isDirty || updateBranding.isPending}>
                {updateBranding.isPending ? 'Saving…' : 'Save branding'}
              </Button>
            </div>
          </div>

          <aside
            className={`project-branding-settings__preview project-branding-settings__preview--${previewTheme}`}
            aria-label="Auth-UI preview"
            style={{
              ['--brand-background' as string]: activePreviewColors.background_color,
              ['--brand-foreground' as string]: activePreviewColors.foreground_color,
            }}
          >
            <div className="project-branding-settings__preview-header">
              <span className="project-branding-settings__preview-tag">Live preview</span>
              <div
                className="project-branding-settings__preview-theme-toggle"
                role="radiogroup"
                aria-label="Preview theme"
              >
                {(['light', 'dark'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={previewTheme === t}
                    className={previewTheme === t ? 'is-selected' : ''}
                    onClick={() => setPreviewTheme(t)}
                  >
                    {t === 'dark' ? <Moon size={12} /> : <Sun size={12} />}
                    {t === 'dark' ? 'Dark' : 'Light'}
                  </button>
                ))}
              </div>
            </div>
            {layout === 'split' ? (
              <div
                className="project-branding-settings__preview-split"
                style={
                  { '--preview-brand': activePreviewColors.primary_color } as React.CSSProperties
                }
              >
                <div className="project-branding-settings__preview-split-brand" aria-hidden="true">
                  {branding.logo_url && (
                    <img
                      src={branding.logo_url}
                      alt=""
                      className="project-branding-settings__preview-split-logo"
                    />
                  )}
                </div>
                <div className="project-branding-settings__preview-split-form">
                  <PreviewCardContents
                    appName={previewAppName}
                    colors={activePreviewColors}
                    logoUrl={null}
                  />
                </div>
              </div>
            ) : (
              <div className="project-branding-settings__preview-card">
                <PreviewCardContents
                  appName={previewAppName}
                  colors={activePreviewColors}
                  logoUrl={branding.logo_url}
                />
              </div>
            )}
          </aside>
        </div>
      </form>
    </FormSection>
  );
}

interface PreviewCardContentsProps {
  appName: string;
  colors: Record<BrandingColorKey, string>;
  logoUrl: string | null;
}

function PreviewCardContents({ appName, colors, logoUrl }: PreviewCardContentsProps) {
  return (
    <>
      {logoUrl && <img src={logoUrl} alt="" className="project-branding-settings__preview-logo" />}
      <h3 className="project-branding-settings__preview-title">Sign in to {appName}</h3>
      <p className="project-branding-settings__preview-subtitle">
        Use your email and password to continue.
      </p>
      <div className="project-branding-settings__preview-field">
        <span>Email</span>
        <input
          type="email"
          readOnly
          value="user@example.com"
          style={{
            borderColor: colors.primary_color,
            boxShadow: `0 0 0 3px ${colors.primary_color}26`,
          }}
        />
      </div>
      <div className="project-branding-settings__preview-field">
        <span>Password</span>
        <input type="password" readOnly value="•••••••••" />
      </div>
      <button
        type="button"
        tabIndex={-1}
        className="project-branding-settings__preview-submit"
        style={{ background: colors.primary_color, color: '#ffffff' }}
      >
        Sign in
      </button>
      <p className="project-branding-settings__preview-error" style={{ color: colors.error_color }}>
        Example error message.
      </p>
    </>
  );
}
